import type React from 'react'
import {memo, useState, useEffect, useEffectEvent, useLayoutEffect, useRef, useCallback} from 'react'
import {Overlay, useFocusTrap, type TouchOrMouseEvent, IconButton, registerPortalRoot} from '@primer/react'
import {XIcon} from '@primer/octicons-react'
import {copilotChatSearchInputId, copilotChatTextAreaId} from '@github-ui/copilot-chat-helpers/constants'
import {useSlots} from '@primer/react/experimental'

import styles from './SidePanel.module.css'
import {clsx} from 'clsx'

type SidePanelProps = {
  externalFocusTrapDisabled?: boolean
  bodyScrollLockDisabled?: boolean
  children: React.ReactNode
  initialFocusRef?: React.RefObject<HTMLElement | null>
  returnFocusRef: React.RefObject<HTMLElement | null>
  /** In order to support transitions, the side panel should always be rendered but visibility should be controlled via this prop. */
  open: boolean
  onClose: (
    e: TouchOrMouseEvent | globalThis.KeyboardEvent | React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => void
  /** Called after the panel's close transition completes and its contents are no longer rendered. */
  onAfterClose?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  topOffset?: string | number
  width?: string
  panelClassName?: string
  headerClassName?: string
  closeButtonClassName?: string
  /** Defines wether the default close element should be the close button (default), overlay, or the first focusable child element within the side-panel. */
  defaultCloseElement?: 'button' | 'overlay' | 'first-child'
  // Either a label string _or_ a labelledby reference is required
} & ({'aria-label': string; 'aria-labelledby'?: undefined} | {'aria-labelledby': string; 'aria-label'?: undefined})

const TRANSITION_DURATION_MS = 200
export const SIDE_PANEL_PORTAL_ROOT = 'sidePanelPortalRoot'

const SidePanelBase = memo(
  ({
    children,
    initialFocusRef,
    returnFocusRef,
    open,
    onClose,
    onAfterClose,
    onKeyDown,
    topOffset = 0,
    width,
    panelClassName,
    headerClassName,
    closeButtonClassName,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    defaultCloseElement = 'button',
    externalFocusTrapDisabled = false,
    bodyScrollLockDisabled = false,
  }: SidePanelProps) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const portalCallbackRef = useCallback((node: HTMLDivElement | null) => {
      if (node) {
        registerPortalRoot(node, SIDE_PANEL_PORTAL_ROOT)
      }
    }, [])

    // We need to disable/enable the focus trap so that it resets the focus trap when the modal is opened and the
    // container element changes, otherwise the sentinel elements will not be inserted and focus won't be trapped.

    const [focusTrapDisabled, setFocusTrapDisabled] = useState(
      // eslint-disable-next-line react-hooks/refs
      containerRef.current === null || !open || externalFocusTrapDisabled,
    )
    const {initialFocusRef: internalInitialFocusRef} = useFocusTrap({
      initialFocusRef,
      containerRef,
      disabled: externalFocusTrapDisabled || focusTrapDisabled,
    })

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const [rendered, setRendered] = useState(open)
    const [visible, setVisible] = useState(open)
    const handleAfterClose = useEffectEvent(() => onAfterClose?.())

    const [slots, childrenWithoutSlots] = useSlots(children, {
      heading: Heading,
    })

    useEffect(() => {
      let timeoutId: ReturnType<typeof setTimeout>
      if (open) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setRendered(true)
        timeoutId = setTimeout(() => setVisible(true))
      } else if (rendered) {
        setVisible(false)
        timeoutId = setTimeout(() => {
          setRendered(false)
          handleAfterClose()
        }, TRANSITION_DURATION_MS)
      }

      return () => clearTimeout(timeoutId)
    }, [open, rendered])

    const onWindowClick = useCallback((e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.id === copilotChatTextAreaId || target.id === copilotChatSearchInputId) {
        setFocusTrapDisabled(true)
        window.setTimeout(() => target.focus())
      }
    }, [])

    const onPanelKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && e.shiftKey && e.key === 'C') {
          const input = document.querySelector(`#${copilotChatTextAreaId}, #${copilotChatSearchInputId}`) as HTMLElement
          if (input) {
            setFocusTrapDisabled(true)
            window.setTimeout(() => input.focus())
          }
        }
        if (onKeyDown) {
          onKeyDown(e)
        }
      },
      [onKeyDown],
    )

    useEffect(() => {
      window.addEventListener('click', onWindowClick)

      return () => {
        window.removeEventListener('click', onWindowClick)
      }
    }, [onWindowClick])

    // useLayoutEffect ensures the body scroll lock is applied synchronously before the
    // browser paints, preventing a brief flash of the body scrollbar alongside the panel.
    useLayoutEffect(() => {
      if (bodyScrollLockDisabled) return

      const bodyOverflowStyle = document.body.style.overflow || ''
      // If the body is already set to overflow: hidden, it likely means
      // that there is already a modal open. In that case, we should bail
      // so we don't re-enable scroll after the second dialog is closed.
      if (bodyOverflowStyle === 'hidden') {
        return
      }

      // Measure the scrollbar width before hiding overflow so we can compensate
      // with padding-right to prevent the underlying content from shifting.
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      const inlineBodyPaddingRight = document.body.style.paddingRight || ''
      const computedBodyStyle = window.getComputedStyle(document.body)
      const computedPaddingRight = computedBodyStyle ? computedBodyStyle.paddingRight : ''

      document.body.style.overflow = 'hidden'

      if (scrollbarWidth > 0) {
        const existingPadding = parseFloat(computedPaddingRight) || 0
        document.body.style.paddingRight = `${existingPadding + scrollbarWidth}px`
      }

      return () => {
        document.body.style.overflow = bodyOverflowStyle
        document.body.style.paddingRight = inlineBodyPaddingRight
      }
    }, [bodyScrollLockDisabled])

    const onFocus = useCallback(() => {
      if (focusTrapDisabled) {
        setFocusTrapDisabled(!open)
      }
    }, [focusTrapDisabled, open])

    if (!rendered) return null

    const Backdrop = defaultCloseElement === 'overlay' ? 'button' : 'div'
    const resolvedTopOffset = typeof topOffset === 'number' ? `${topOffset}px` : topOffset

    return (
      <Overlay
        initialFocusRef={internalInitialFocusRef}
        returnFocusRef={returnFocusRef}
        onKeyDown={onPanelKeyDown}
        onEscape={onClose}
        onClickOutside={() => {
          // We don't need to handle clicks outside because we handle clicks on the backdrop instead. Handling clicks
          // outside can cause bugs where clicks on dialogs over the side panel (ie, a confirm dialog or menu) register
          // as clicks outside the side panel and attempt to close it.
        }}
        ref={containerRef}
        role="dialog"
        aria-modal
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        style={{'--transition-duration': `${TRANSITION_DURATION_MS}ms`, '--top-offset': resolvedTopOffset}}
        onFocus={onFocus}
        className={styles.Overlay}
      >
        <Backdrop
          onClick={onClose}
          aria-label={defaultCloseElement === 'overlay' ? 'Close' : undefined}
          className={styles.Backdrop}
        />
        <div
          style={width ? {'--side-panel-width': width} : undefined}
          className={clsx(
            styles.sidePanel,
            panelClassName,
            (visible || prefersReducedMotion) && styles.openSidePanel,
            visible && styles.isVisible,
          )}
        >
          <div className={clsx(styles.Box_2, headerClassName)}>
            <div className="flex-1 min-width-0">{slots.heading}</div>
            <div>
              {defaultCloseElement === 'button' && (
                <IconButton
                  variant="invisible"
                  icon={XIcon}
                  aria-label="Close"
                  onClick={onClose}
                  className={clsx(styles.IconButton, closeButtonClassName)}
                />
              )}
            </div>
          </div>
          {childrenWithoutSlots}
          <div ref={portalCallbackRef} data-testid="side-panel-portal-root" />
        </div>
      </Overlay>
    )
  },
)

SidePanelBase.displayName = 'SidePanel'

type SidePanelHeadingProps = {
  something?: string
}

const Heading = ({children}: React.PropsWithChildren<SidePanelHeadingProps>) => {
  return <div>{children}</div>
}

export const SidePanel = Object.assign(SidePanelBase, {Heading})
